import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Modal,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PrintIcon from "@mui/icons-material/Print";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { useNavigate, useParams } from "react-router-dom";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import InfoInvoice from "./InvoiceForm/InfoInvoice";
import InvoiceDetail from "./InvoiceForm/InvoiceDetail";

const getCookie = (name) => {
  const v = `; ${document.cookie}`;
  const parts = v.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
};

const Invoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const realId = id.includes("-") ? id.split("-")[1] : id;

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [doc, setDoc] = useState(null);

  const [sendOpen, setSendOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendInfo, setSendInfo] = useState({ documento: "", razon_social: "", correo: "" });
  const [correoInput, setCorreoInput] = useState("");
  const [correoError, setCorreoError] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  useEffect(() => {
    fetch(`/facturas/documentos/${id}/`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) => setDoc(j))
      .catch(() => setDoc(null));
  }, [id]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const descargar = (url, nombre) => {
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = nombre;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });
  };

  const esVenta = doc && doc.origen === "VENTA";

  const ensureCsrf = async () => {
    await fetch("/facturas/csrf/", { credentials: "include" }).catch(() => {});
  };

  const abrirModalEnvio = async () => {
    setCorreoError("");
    setSendInfo({ documento: "", razon_social: "", correo: "" });
    setCorreoInput("");
    setSendOpen(true);
    setSendLoading(true);

    try {
      await ensureCsrf();
      const csrftoken = getCookie("csrftoken");

      const r = await fetch("/facturas/enviar/info/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ documento_id: realId }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo obtener la info");

      setSendInfo({
        documento: data.documento || "",
        razon_social: data.razon_social || "",
        correo: data.correo || "",
      });
      setCorreoInput(data.correo || "");
    } catch (err) {
      setSnack({ open: true, msg: err.message || "Error al cargar info", sev: "error" });
      setSendOpen(false);
    } finally {
      setSendLoading(false);
    }
  };

  const enviarCorreo = async () => {
    const email = (correoInput || "").trim();
    if (!email || !email.includes("@")) {
      setCorreoError("Correo inválido");
      return;
    }
    setCorreoError("");
    setSendBusy(true);

    try {
      await ensureCsrf();
      const csrftoken = getCookie("csrftoken");

      const r = await fetch("/facturas/enviar/enviar/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ documento_id: realId, correo: email }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo enviar el correo");

      setSnack({ open: true, msg: "Correo enviado correctamente", sev: "success" });
      setSendOpen(false);
    } catch (err) {
      setSnack({ open: true, msg: err.message || "Error al enviar", sev: "error" });
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <InfoInvoice />

          <InvoiceDetail doc={doc} documentoId={realId} />

          {esVenta && doc && doc.autorizacion && (
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                startIcon={<MailOutlineIcon />}
                variant="contained"
                sx={{ bgcolor: "#0870A2" }}
                onClick={abrirModalEnvio}
              >
                Enviar
              </Button>

              <Button
                startIcon={<PrintIcon />}
                variant="contained"
                sx={{ bgcolor: "#0870A2" }}
                onClick={() => descargar(`/facturas/pdf/${realId}/`, `Factura_${doc.numero_documento}.pdf`)}
              >
                Imprimir
              </Button>

              <Button
                startIcon={<ConfirmationNumberIcon />}
                variant="contained"
                sx={{ bgcolor: "#0870A2" }}
                onClick={() => descargar(`/facturas/ticket/${realId}/`, `Ticket_${doc.numero_documento}.pdf`)}
              >
                Ticket
              </Button>
            </Stack>
          )}
        </Box>
      </Box>

      <Modal open={sendOpen} onClose={sendBusy ? null : () => setSendOpen(false)} disableScrollLock>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 520,
            maxWidth: "92vw",
          }}
        >
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1, color: "#1565C0" }}>
              Enviar factura por correo
            </Typography>

            {sendLoading ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Cargando información…</Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ mb: 0.5, fontWeight: 700 }}>
                  Se enviará la factura {sendInfo.documento || ""}
                </Typography>
                <Typography sx={{ mb: 2, color: "#666" }}>{sendInfo.razon_social || ""}</Typography>

                <TextField
                  label="Correo de destino"
                  value={correoInput}
                  onChange={(e) => setCorreoInput(e.target.value)}
                  fullWidth
                  error={Boolean(correoError)}
                  helperText={correoError || "Verifica o corrige el correo antes de enviar"}
                />

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSendOpen(false)}
                    disabled={sendBusy}
                    sx={{ borderColor: "#1565C0", color: "#1565C0" }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    variant="contained"
                    onClick={enviarCorreo}
                    disabled={sendBusy}
                    sx={{ bgcolor: "#1565C0" }}
                  >
                    {sendBusy ? "Enviando..." : "Enviar"}
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Box>
      </Modal>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoice;
